import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import RequireAuth from '@/components/RequireAuth';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/pages/Login';
import ProjectsPage from '@/pages/Projects';
import ProjectDetailPage from '@/pages/ProjectDetail';
import ProjectEstimatingPage from '@/pages/ProjectEstimating';
import EstimateViewer from '@/pages/EstimateViewer';
import ProjectCostControl from '@/pages/ProjectCostControl';
import EstimatePrint from '@/pages/EstimatePrint';
import SuppliersPage from '@/pages/Suppliers';
import SupplierDetail from '@/pages/SupplierDetail';
import ApprovalsPage from '@/pages/Approvals';
import ApprovalDetail from '@/pages/ApprovalDetail';
import PurchaseRequestsPage from '@/pages/PurchaseRequests';
import CostingLibraryPage from '@/pages/CostingLibrary';
import PurchaseRequestDetail from '@/pages/PurchaseRequestDetail';
import PurchaseOrderDetail from '@/pages/PurchaseOrderDetail';
import PurchaseOrderPrint from '@/pages/PurchaseOrderPrint';
import SupplierInvoiceDetail from '@/pages/SupplierInvoiceDetail';
import ProjectFinancial from '@/pages/ProjectFinancial';
import ProjectPackages from '@/pages/ProjectPackages';
import PackageDetail from '@/pages/PackageDetail';
import LabourPage from '@/pages/Labour';
import TimesheetDetail from '@/pages/TimesheetDetail';
import EmployeeProfile from '@/pages/EmployeeProfile';
import InvoiceInboxPage from '@/pages/InvoiceInbox';
import { environmentLabel } from '@/lib/supabase';
import ImportReviewPage from '@/pages/ImportReview';
import SiteDiaryPage from '@/pages/SiteDiary';
import SiteDiaryDetail from '@/pages/SiteDiaryDetail';
import TasksPage from '@/pages/Tasks';
import Quotations from '@/pages/Quotations';
import QuotationDetail from '@/pages/QuotationDetail';
import CompanySettingsPage from '@/pages/CompanySettings';
import TeamAccessPage from '@/pages/TeamAccess';
import ResetPassword from '@/pages/ResetPassword';
import PortalLayout from '@/components/portal/PortalLayout';
import PortalHome from '@/pages/portal/PortalHome';
import PortalProject from '@/pages/portal/PortalProject';
import PortalWelcome from '@/pages/portal/PortalWelcome';
import ProjectPortalAdmin from '@/pages/ProjectPortalAdmin';
import ProjectSafety from '@/pages/ProjectSafety';
import SafetyOverview from '@/pages/SafetyOverview';
import SiteDrawingsPage from '@/pages/SiteDrawings';
import EstimatingHome from '@/pages/EstimatingHome';
import CommandCentre from '@/pages/CommandCentre';
import FinancialControl from '@/pages/FinancialControl';
import FinanceRecords from '@/pages/FinanceRecords';
import SubcontractorPayments from '@/pages/SubcontractorPayments';
import Clients from '@/pages/Clients';
import ClientDetail from '@/pages/ClientDetail';
import ClientInvoices from '@/pages/ClientInvoices';
import ClientInvoiceDetail from '@/pages/ClientInvoiceDetail';
import MyExpenses from '@/pages/me/MyExpenses';
import ExpenseApprovals from '@/pages/expenses/ExpenseApprovals';
import BuildupImport from '@/pages/library/BuildupImport';
import MyWork from '@/pages/me/MyWork';
import SnagInspect from '@/pages/snag/SnagInspect';
import SnagNewInspection from '@/pages/snag/SnagNewInspection';
import { SnagOverview, SnagInspections, SnagReports, SnagClients, SnagBilling } from '@/pages/snag/SnagPages';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/portal/welcome" element={<PortalWelcome />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal" element={<RequireAuth><PortalLayout /></RequireAuth>}>
            <Route index element={<PortalHome />} />
            <Route path="projects/:id" element={<PortalProject />} />
          </Route>
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<CommandCentre />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/estimating" element={<EstimatingHome />} />
            <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
            <Route path="/me/expenses" element={<MyExpenses />} />
            <Route path="/me/work" element={<MyWork />} />
            <Route path="/expenses/approvals" element={<ExpenseApprovals />} />
            <Route path="/library/buildups/import" element={<BuildupImport />} />
            <Route path="/financial" element={<FinancialControl />} />
            <Route path="/finance/records" element={<FinanceRecords />} />
            <Route path="/finance/subcontractors" element={<SubcontractorPayments />} />
            <Route path="/finance/clients" element={<Clients />} />
            <Route path="/finance/clients/:clientId" element={<ClientDetail />} />
            <Route path="/finance/quotations" element={<Quotations />} />
            <Route path="/finance/quotations/:quotationId" element={<QuotationDetail />} />
            <Route path="/finance/invoices" element={<ClientInvoices />} />
            <Route path="/finance/invoices/:invoiceId" element={<ClientInvoiceDetail />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/estimating" element={<ProjectEstimatingPage />} />
            <Route path="/projects/:id/estimating/:importId" element={<EstimateViewer />} />
            <Route path="/projects/:id/estimating/:importId/print" element={<EstimatePrint />} />
            <Route path="/purchasing/orders/:poId/print" element={<PurchaseOrderPrint />} />
            <Route path="/projects/:id/cost-control" element={<ProjectCostControl />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/suppliers/:supplierId" element={<SupplierDetail />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/approvals/:requestId" element={<ApprovalDetail />} />
            <Route path="/purchasing" element={<PurchaseRequestsPage />} />
            <Route path="/purchasing/requests/:requestId" element={<PurchaseRequestDetail />} />
            <Route path="/purchasing/orders/:poId" element={<PurchaseOrderDetail />} />
            <Route path="/purchasing/invoices/:invoiceId" element={<SupplierInvoiceDetail />} />
            <Route path="/projects/:id/financial" element={<ProjectFinancial />} />
            <Route path="/projects/:id/packages" element={<ProjectPackages />} />
            <Route path="/projects/:id/packages/:costCodeId" element={<PackageDetail />} />
            <Route path="/costing" element={<CostingLibraryPage />} />
            <Route path="/labour" element={<LabourPage />} />
            <Route path="/labour/timesheets/:timesheetId" element={<TimesheetDetail />} />
            <Route path="/labour/employees/:employeeId" element={<EmployeeProfile />} />
            <Route path="/invoices" element={<InvoiceInboxPage />} />
            <Route path="/invoices/:importId" element={<ImportReviewPage />} />
            <Route path="/site-diary" element={<SiteDiaryPage />} />
            <Route path="/site-diary/:diaryId" element={<SiteDiaryDetail />} />
            <Route path="/snag" element={<SnagOverview />} />
            <Route path="/snag/inspections" element={<SnagInspections />} />
            <Route path="/snag/inspections/new" element={<SnagNewInspection />} />
            <Route path="/snag/inspections/:id" element={<SnagInspect />} />
            <Route path="/snag/reports" element={<SnagReports />} />
            <Route path="/snag/clients" element={<SnagClients />} />
            <Route path="/snag/billing" element={<SnagBilling />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/settings" element={<CompanySettingsPage />} />
            <Route path="/settings/team" element={<TeamAccessPage />} />
            <Route path="/projects/:id/portal" element={<ProjectPortalAdmin />} />
            <Route path="/projects/:id/safety" element={<ProjectSafety />} />
            <Route path="/safety" element={<SafetyOverview />} />
            <Route path="/drawings" element={<SiteDrawingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      {environmentLabel && <div data-testid="env-banner" className="fixed bottom-0 inset-x-0 z-[100] bg-amber-500 text-black text-center text-xs font-semibold tracking-widest py-1">{environmentLabel} ENVIRONMENT — not production</div>}
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
